import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";

interface BatchEntry {
  id?: number;
  batchOrderId: number | null;
  jobId: number | null;
  mixDesignId: number | null;
  logDate: Date;
  shift: "day" | "night" | "swing";
  supervisor: number | null;
  plantOperators: number[];

  // Batch Metrics
  yardsPlanned: number;
  yardsProduced: number;
  yardsDelivered: number;
  yardsWasted: number;
  loadsCompleted: number;

  // Time Tracking
  shiftStartTime: string;
  shiftEndTime: string;
  breakTime: number; // minutes
  downtime: number; // minutes
  downtimeReason: string;

  // Materials Used
  materialsUsed: {
    materialId: number;
    quantityUsed: number;
    waste: number;
  }[];

  // Quality Metrics
  slumpTests: {
    batchNumber: string;
    slumpTarget: number;
    slumpActual: number;
    airContent: number;
    temperature: number;
    testTime: string;
    passedSpec: boolean;
  }[];
  qualityNotes: string;

  // Equipment
  mixersUsed: string[];
  trucksDispatched: number[];
  maintenanceIssues: string;
  plantIssues: string;

  // Notes & Comments
  operationsNotes: string;
  safetyIncidents: string;
  improvementSuggestions: string;

  // Weather Impact (critical for concrete)
  weatherConditions: string;
  temperatureHigh: number | null;
  temperatureLow: number | null;
  humidity: number | null;
  windSpeed: number | null;
  weatherImpact: boolean;
  weatherDelays: number; // minutes
}

class DailyBatchLogWorkflowStore {
  rootStore: RootStore;

  // Observables
  loading = false;
  error: string | null = null;
  success: string | null = null;
  activeStep = 0;
  selectedDate: Date | null = new Date();
  batchItems: any[] = [];
  scanMode = false;
  scannedCode = "";

  // Batch Entry Data
  entryData: BatchEntry;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    this.entryData = this.getDefaultEntry();
    makeAutoObservable(this, { rootStore: false });
  }

  getDefaultEntry = (): BatchEntry => ({
    batchOrderId: null,
    jobId: null,
    mixDesignId: null,
    logDate: new Date(),
    shift: "day",
    supervisor: null,
    plantOperators: [],

    yardsPlanned: 0,
    yardsProduced: 0,
    yardsDelivered: 0,
    yardsWasted: 0,
    loadsCompleted: 0,

    shiftStartTime: "07:00",
    shiftEndTime: "15:00",
    breakTime: 30,
    downtime: 0,
    downtimeReason: "",

    materialsUsed: [],

    slumpTests: [],
    qualityNotes: "",

    mixersUsed: [],
    trucksDispatched: [],
    maintenanceIssues: "",
    plantIssues: "",

    operationsNotes: "",
    safetyIncidents: "",
    improvementSuggestions: "",

    weatherConditions: "",
    temperatureHigh: null,
    temperatureLow: null,
    humidity: null,
    windSpeed: null,
    weatherImpact: false,
    weatherDelays: 0,
  });

  // Actions
  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  setActiveStep = (step: number) => {
    this.activeStep = step;
  };

  nextStep = () => {
    if (this.validateCurrentStep()) {
      this.activeStep++;
    }
  };

  prevStep = () => {
    this.activeStep--;
  };

  updateEntryData = <K extends keyof BatchEntry>(
    field: K,
    value: BatchEntry[K]
  ) => {
    this.entryData[field] = value;
  };

  addPlantOperator = (userId: number) => {
    if (!this.entryData.plantOperators.includes(userId)) {
      this.entryData.plantOperators = [...this.entryData.plantOperators, userId];
    }
  };

  removePlantOperator = (userId: number) => {
    this.entryData.plantOperators = this.entryData.plantOperators.filter(id => id !== userId);
  };

  addMaterialUsed = (materialId: number, quantityUsed: number, waste: number = 0) => {
    const existing = this.entryData.materialsUsed.find(m => m.materialId === materialId);
    if (existing) {
      existing.quantityUsed = quantityUsed;
      existing.waste = waste;
    } else {
      this.entryData.materialsUsed = [
        ...this.entryData.materialsUsed,
        { materialId, quantityUsed, waste }
      ];
    }
  };

  removeMaterialUsed = (materialId: number) => {
    this.entryData.materialsUsed = this.entryData.materialsUsed.filter(
      m => m.materialId !== materialId
    );
  };

  addMixer = (mixer: string) => {
    if (!this.entryData.mixersUsed.includes(mixer)) {
      this.entryData.mixersUsed = [...this.entryData.mixersUsed, mixer];
    }
  };

  removeMixer = (mixer: string) => {
    this.entryData.mixersUsed = this.entryData.mixersUsed.filter(
      m => m !== mixer
    );
  };

  addTruck = (truckId: number) => {
    if (!this.entryData.trucksDispatched.includes(truckId)) {
      this.entryData.trucksDispatched = [...this.entryData.trucksDispatched, truckId];
    }
  };

  removeTruck = (truckId: number) => {
    this.entryData.trucksDispatched = this.entryData.trucksDispatched.filter(
      id => id !== truckId
    );
  };

  addSlumpTest = (test: BatchEntry['slumpTests'][0]) => {
    this.entryData.slumpTests = [...this.entryData.slumpTests, test];
  };

  removeSlumpTest = (index: number) => {
    this.entryData.slumpTests = this.entryData.slumpTests.filter((_, i) => i !== index);
  };

  updateSlumpTest = (index: number, updates: Partial<BatchEntry['slumpTests'][0]>) => {
    this.entryData.slumpTests = this.entryData.slumpTests.map((test, i) =>
      i === index ? { ...test, ...updates } : test
    );
  };

  setSelectedDate = (date: Date | null) => {
    this.selectedDate = date;
    this.loadBatchData();
  };

  setScanMode = (mode: boolean) => {
    this.scanMode = mode;
    if (!mode) {
      this.scannedCode = "";
    }
  };

  setScannedCode = (code: string) => {
    this.scannedCode = code;
  };

  // Validation
  validateCurrentStep = (): boolean => {
    this.setError(null);

    switch (this.activeStep) {
      case 0: // Basic Information
        if (!this.entryData.batchOrderId && !this.entryData.jobId) {
          this.setError("Please select a batch order or job");
          return false;
        }
        if (!this.entryData.mixDesignId) {
          this.setError("Please select a mix design");
          return false;
        }
        if (!this.entryData.supervisor) {
          this.setError("Please select a supervisor");
          return false;
        }
        if (this.entryData.plantOperators.length === 0) {
          this.setError("Please add at least one plant operator");
          return false;
        }
        break;

      case 1: // Batch Metrics
        if (this.entryData.yardsProduced < 0) {
          this.setError("Yards produced cannot be negative");
          return false;
        }
        if (this.entryData.yardsProduced > this.entryData.yardsPlanned * 1.2) {
          this.setError("Yards produced seems unusually high compared to planned");
          return false;
        }
        if (this.entryData.yardsWasted < 0) {
          this.setError("Yards wasted cannot be negative");
          return false;
        }
        break;

      case 2: // Time & Materials
        if (!this.entryData.shiftStartTime || !this.entryData.shiftEndTime) {
          this.setError("Please specify shift start and end times");
          return false;
        }
        if (this.entryData.downtime > 480) { // 8 hours
          this.setError("Downtime cannot exceed 8 hours");
          return false;
        }
        if (this.entryData.downtime > 0 && !this.entryData.downtimeReason) {
          this.setError("Please specify reason for downtime");
          return false;
        }
        break;

      case 3: // Quality & Equipment
        if (this.entryData.mixersUsed.length === 0) {
          this.setError("Please specify which mixers were used");
          return false;
        }
        if (this.entryData.trucksDispatched.length === 0) {
          this.setError("Please specify which trucks were dispatched");
          return false;
        }
        // Validate slump tests
        for (const test of this.entryData.slumpTests) {
          if (test.slumpActual < 0 || test.slumpActual > 12) {
            this.setError("Slump values must be between 0 and 12 inches");
            return false;
          }
        }
        break;
    }

    return true;
  };

  // Async actions
  loadInitialData = async () => {
    this.setLoading(true);
    this.setError(null);

    try {
      const jobStore = this.rootStore.jobStore as ServiceStore;
      const userStore = this.rootStore.userStore as ServiceStore;
      const materialsStore = this.rootStore.materialsStore as ServiceStore;
      const mixDesignsStore = this.rootStore.mixDesignsStore as ServiceStore;
      const trucksStore = this.rootStore.trucksStore as ServiceStore;

      await Promise.all([
        jobStore?.find({ query: { archived: false } }),
        userStore?.find(),
        materialsStore?.find(),
        mixDesignsStore?.find(),
        trucksStore?.find({ query: { status: "active" } }),
      ]);
    } catch (err: any) {
      runInAction(() => {
        this.setError("Failed to load initial data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  loadBatchData = async () => {
    if (!this.selectedDate) return;

    this.loading = true;
    try {
      // TODO: Load batch orders for selected date when service is available
      // For now, just clear the list
      runInAction(() => {
        this.batchItems = [];
      });
    } catch (err) {
      runInAction(() => {
        this.error = "Failed to load batch data";
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  };

  submitBatchLog = async () => {
    if (!this.validateCurrentStep()) {
      return;
    }

    this.setLoading(true);
    this.setError(null);

    try {
      // TODO: Create batch log entry when backend service is available
      // This would create a daily_batch_logs record with all the collected data

      // Update material inventory if materials were used
      if (this.entryData.materialsUsed.length > 0) {
        // TODO: Implement material consumption tracking
        console.log("Would update material inventory:", this.entryData.materialsUsed);
      }

      runInAction(() => {
        this.setSuccess("Batch log submitted successfully!");
        this.activeStep = 5; // Success step
      });

    } catch (err: any) {
      runInAction(() => {
        this.setError(err.message || "Failed to submit batch log");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  };

  reset = () => {
    this.loading = false;
    this.error = null;
    this.success = null;
    this.activeStep = 0;
    this.selectedDate = new Date();
    this.batchItems = [];
    this.scanMode = false;
    this.scannedCode = "";
    this.entryData = this.getDefaultEntry();
  };

  // Computed values
  get jobs() {
    const jobStore = this.rootStore.jobStore as ServiceStore;
    return jobStore?.entities || [];
  }

  get users() {
    const userStore = this.rootStore.userStore as ServiceStore;
    return userStore?.entities || [];
  }

  get materials() {
    const materialsStore = this.rootStore.materialsStore as ServiceStore;
    return materialsStore?.entities || [];
  }

  get mixDesigns() {
    const mixDesignsStore = this.rootStore.mixDesignsStore as ServiceStore;
    return mixDesignsStore?.entities || [];
  }

  get trucks() {
    const trucksStore = this.rootStore.trucksStore as ServiceStore;
    return trucksStore?.entities || [];
  }

  get selectedJob() {
    if (!this.entryData.jobId) return null;
    return this.jobs.find((j: any) => j.id === this.entryData.jobId);
  }

  get selectedMixDesign() {
    if (!this.entryData.mixDesignId) return null;
    return this.mixDesigns.find((md: any) => md.id === this.entryData.mixDesignId);
  }

  get supervisorName() {
    if (!this.entryData.supervisor) return "";
    const supervisor = this.users.find((u: any) => u.id === this.entryData.supervisor);
    return supervisor?.name || "";
  }

  get plantOperatorNames() {
    return this.entryData.plantOperators.map(id => {
      const user = this.users.find((u: any) => u.id === id);
      return user?.name || `User ${id}`;
    });
  }

  calculateShiftHours = (): number => {
    if (!this.entryData.shiftStartTime || !this.entryData.shiftEndTime) return 8;

    const start = new Date(`2000-01-01T${this.entryData.shiftStartTime}:00`);
    const end = new Date(`2000-01-01T${this.entryData.shiftEndTime}:00`);

    let hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    if (hours < 0) hours += 24; // Handle overnight shifts

    // Subtract break time, downtime, and weather delays
    hours -= (this.entryData.breakTime + this.entryData.downtime + this.entryData.weatherDelays) / 60;

    return Math.max(0, hours);
  };

  calculatePercentComplete = (): number => {
    if (this.entryData.yardsPlanned === 0) return 0;
    return Math.round((this.entryData.yardsDelivered / this.entryData.yardsPlanned) * 100);
  };

  calculateEfficiency = (): number => {
    if (this.entryData.yardsPlanned === 0) return 0;
    return Math.round((this.entryData.yardsProduced / this.entryData.yardsPlanned) * 100);
  };

  calculateWasteRate = (): number => {
    if (this.entryData.yardsProduced === 0) return 0;
    return Math.round((this.entryData.yardsWasted / this.entryData.yardsProduced) * 100);
  };

  get efficiencyColor() {
    const efficiency = this.calculateEfficiency();
    if (efficiency >= 95) return "success";
    if (efficiency >= 85) return "warning";
    return "error";
  }

  get wasteRateColor() {
    const wasteRate = this.calculateWasteRate();
    if (wasteRate <= 2) return "success";
    if (wasteRate <= 5) return "warning";
    return "error";
  }

  get slumpTestPassRate() {
    if (this.entryData.slumpTests.length === 0) return 100;
    const passed = this.entryData.slumpTests.filter(t => t.passedSpec).length;
    return Math.round((passed / this.entryData.slumpTests.length) * 100);
  }

  get slumpTestPassRateColor() {
    const passRate = this.slumpTestPassRate;
    if (passRate >= 95) return "success";
    if (passRate >= 85) return "warning";
    return "error";
  }
}

export default DailyBatchLogWorkflowStore;
